import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  Card,
  Upload,
  message,
  Spin,
  Modal,
  Table,
  Space,
  Popconfirm,
  Tag,
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  UploadOutlined,
  DollarOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { expensesApi, expenseCategoriesApi } from '../../utils/api';
import { handleFormSubmitError } from '../../utils/errorHandler';
import PersianDatePicker from '../../components/PersianDatePicker';
import { translateRoleName } from '../../utils/formHelpers';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultCurrencyId } from '../../utils/currencyUtils';
import { formatJalaliApiDate } from '../../utils/dateUtils';

const { Option } = Select;
const { TextArea } = Input;

const AddExpense = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const presetProjectId = searchParams.get('project_id');
  const returnTo = location.state?.returnTo;
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lockProject, setLockProject] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banks, setBanks] = useState([]);
  const [billFile, setBillFile] = useState(null);

  // Category Modal States
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [categoryForm] = Form.useForm();
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);

  useEffect(() => {
    fetchCreateData();
  }, [presetProjectId]);

  const fetchCreateData = async () => {
    setLoading(true);
    try {
      const res = await expensesApi.getCreateData(
        presetProjectId ? { project_id: presetProjectId } : {},
      );
      console.log('Expense Create Data:', res);
      if (res?.data) {
        const selectedProject = res.data.selected_project;
        const projectList = [...(res.data.projects || [])];
        if (
          selectedProject?.id != null &&
          !projectList.some((p) => String(p.id) === String(selectedProject.id))
        ) {
          projectList.push({
            id: selectedProject.id,
            project_name: selectedProject.project_name,
          });
        }

        setCurrencies(res.data.currencies || []);
        setEmployees(res.data.employees || []);
        setProjects(projectList);
        setCategories(res.data.categories || []);
        setBanks(res.data.banks || []);

        if (res.data.currencies?.length > 0) {
          const defaultCurrencyId = getDefaultCurrencyId(res.data.currencies);
          const defaultCurrency = res.data.currencies.find((currency) => currency.id === defaultCurrencyId) || res.data.currencies[0];
          form.setFieldValue('currency_id', defaultCurrencyId);
          form.setFieldValue('exchange_rate', defaultCurrency.exchange_rate || 1);
        }

        if (user?.id && (res.data.employees || []).some((employee) => String(employee.id) === String(user.id))) {
          form.setFieldValue('user_id', user.id);
        }

        const resolvedProjectId =
          selectedProject?.id ?? (presetProjectId != null ? Number(presetProjectId) : undefined);
        if (resolvedProjectId != null) {
          form.setFieldValue('project_id', resolvedProjectId);
          setLockProject(Boolean(presetProjectId || selectedProject));
        }
      }
    } catch (error) {
      console.error('Error fetching create data:', error);
      message.error('خطا در دریافت اطلاعات اولیه');
    } finally {
      setLoading(false);
    }
  };

  const handleBillUpload = (info) => {
    if (info.file) {
      setBillFile(info.file);
    }
    return false;
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const purchaseDate = formatJalaliApiDate(values.purchase_date);

      const payload = {
        item_name: values.item_name,
        currency_id: values.currency_id,
        exchange_rate: values.exchange_rate || 1,
        price: values.price,
        purchase_date: purchaseDate,
        user_id: values.user_id || undefined,
        project_id: values.project_id || undefined,
        category_id: values.category_id || undefined,
        purchase_from: values.purchase_from || undefined,
        bank_account_id: values.bank_account_id || undefined,
        description: values.description || undefined,
      };

      if (billFile) {
        payload.bill = billFile;
      }

      console.log('Submitting expense payload:', payload);
      await expensesApi.create(payload);
      message.success('هزینه با موفقیت ثبت شد');
      navigate(returnTo || '/finance/expenses');
    } catch (error) {
      console.error('Error creating expense:', error);
      handleFormSubmitError(form, error, 'خطا در ثبت هزینه');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Category Management ---
  const openCategoryModal = async () => {
    setCategoryModalVisible(true);
    await fetchCategoryCreateData();
  };

  const fetchCategoryCreateData = async () => {
    setCategoryLoading(true);
    try {
      const res = await expenseCategoriesApi.getCreateData();
      console.log('Category Create Data:', res);
      if (res?.data) {
        setAllCategories(res.data.categories || []);
        setAllRoles(res.data.roles || []);
      }
    } catch (error) {
      console.error('Error fetching category create data:', error);
      message.error('خطا در دریافت اطلاعات دسته‌بندی‌ها');
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleCreateCategory = async (values) => {
    setCategorySubmitting(true);
    try {
      if (editingCategoryId) {
        await expenseCategoriesApi.update(editingCategoryId, {
          category_name: values.category_name,
        });
        message.success('دسته‌بندی با موفقیت ویرایش شد');
      } else {
        await expenseCategoriesApi.create(values.category_name, values.roles || []);
        message.success('دسته‌بندی با موفقیت ایجاد شد');
      }
      categoryForm.resetFields();
      setEditingCategoryId(null);
      await fetchCategoryCreateData();
      const res = await expensesApi.getCreateData();
      if (res?.data?.categories) {
        setCategories(res.data.categories);
      }
    } catch (error) {
      console.error('Error saving category:', error);
      message.error('خطا در ذخیره دسته‌بندی');
    } finally {
      setCategorySubmitting(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    setDeletingCategoryId(id);
    try {
      await expenseCategoriesApi.delete(id);
      message.success('دسته‌بندی با موفقیت حذف شد');
      await fetchCategoryCreateData();
      const res = await expensesApi.getCreateData();
      if (res?.data?.categories) {
        setCategories(res.data.categories);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      message.error('خطا در حذف دسته‌بندی');
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const startEditCategory = (category) => {
    setEditingCategoryId(category.id);
    categoryForm.setFieldsValue({
      category_name: category.category_name,
      roles: category.roles?.map((r) => r.role_id) || [],
    });
  };

  const cancelEditCategory = () => {
    setEditingCategoryId(null);
    categoryForm.resetFields();
  };

  const categoryColumns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نام دسته‌بندی</span>,
      dataIndex: 'category_name',
      key: 'category_name',
      render: (text) => (
        <span style={{ fontFamily: 'CustomFont' }}>{text}</span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نقش‌ها</span>,
      dataIndex: 'roles',
      key: 'roles',
      render: (roles) => (
        <div>
          {roles?.map((r) => (
            <Tag key={r.role_id} style={{ fontFamily: 'CustomFont', marginBottom: 2 }}>
              {translateRoleName(r.role?.display_name || r.role?.name)}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => startEditCategory(record)}
            className="text-blue-600"
          />
          <Popconfirm
            title="آیا از حذف این دسته‌بندی مطمئن هستید؟"
            onConfirm={() => handleDeleteCategory(record.id)}
            okText="بله"
            cancelText="خیر"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={deletingCategoryId === record.id}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          {/* Header Section */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                <DollarOutlined className="text-green-600 dark:text-green-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0" style={{ fontFamily: 'CustomFont' }}>
                  ثبت هزینه جدید
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  اطلاعات هزینه را تکمیل و ثبت کنید
                </p>
              </div>
            </div>
            <Space>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate('/finance/expenses')}
                style={{ fontFamily: 'CustomFont' }}
              >
                بازگشت
              </Button>
            </Space>
          </div>

          {/* Content Section */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="dark:bg-gray-800 border-0 shadow-sm">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
                initialValues={{
                  exchange_rate: 1,
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="item_name"
                    label={<span style={{ fontFamily: 'CustomFont' }}>نام هزینه</span>}
                    rules={[
                      { required: true, message: 'لطفاً نام هزینه را وارد کنید' },
                    ]}
                  >
                    <Input
                      placeholder="مثال: خرید سرور"
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="price"
                    label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>}
                    rules={[
                      { required: true, message: 'لطفاً مبلغ را وارد کنید' },
                    ]}
                  >
                    <InputNumber
                      className="w-full"
                      placeholder="مبلغ هزینه"
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={(value) => value.replace(/,/g, '')}
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="currency_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>واحد پول</span>}
                    rules={[
                      { required: true, message: 'لطفاً واحد پول را انتخاب کنید' },
                    ]}
                  >
                    <Select
                      placeholder="انتخاب واحد پول"
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      {currencies.map((cur) => (
                        <Option key={cur.id} value={cur.id}>
                          {cur.currency_symbol} ({cur.currency_code})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="exchange_rate"
                    label={
                      <span style={{ fontFamily: 'CustomFont' }}>نرخ تبدیل</span>
                    }
                  >
                    <InputNumber
                      className="w-full"
                      placeholder="نرخ تبدیل"
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="purchase_date"
                    label={
                      <span style={{ fontFamily: 'CustomFont' }}>تاریخ خرید</span>
                    }
                  >
                    <PersianDatePicker
                      placeholder="انتخاب تاریخ"
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="purchase_from"
                    label={
                      <span style={{ fontFamily: 'CustomFont' }}>خریداری از</span>
                    }
                  >
                    <Input
                      placeholder="نام فروشنده یا مغازه"
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="user_id"
                    label={
                      <span style={{ fontFamily: 'CustomFont' }}>کارمند</span>
                    }
                  >
                    <Select
                      placeholder="انتخاب کارمند"
                      style={{ fontFamily: 'CustomFont' }}
                      showSearch
                      optionFilterProp="children"
                      allowClear
                    >
                      {employees.map((emp) => (
                        <Option key={emp.id} value={emp.id}>
                          {emp.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="project_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>پروژه</span>}
                  >
                    <Select
                      placeholder="انتخاب پروژه"
                      style={{ fontFamily: 'CustomFont' }}
                      showSearch
                      optionFilterProp="children"
                      allowClear={!lockProject}
                      disabled={lockProject}
                    >
                      {projects.map((proj) => (
                        <Option key={proj.id} value={proj.id}>
                          {proj.project_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="category_id"
                    label={
                      <div className="flex items-center justify-between w-full">
                        <span style={{ fontFamily: 'CustomFont' }}>
                          دسته‌بندی هزینه
                        </span>
                        <Button
                          type="link"
                          icon={<SettingOutlined />}
                          onClick={openCategoryModal}
                          style={{ fontFamily: 'CustomFont', padding: 0 }}
                          size="small"
                        >
                          مدیریت دسته‌بندی‌ها
                        </Button>
                      </div>
                    }
                  >
                    <Select
                      placeholder="انتخاب دسته‌بندی"
                      style={{ fontFamily: 'CustomFont' }}
                      allowClear
                    >
                      {categories.map((cat) => (
                        <Option key={cat.id} value={cat.id}>
                          {cat.category_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="bank_account_id"
                    label={
                      <span style={{ fontFamily: 'CustomFont' }}>
                        حساب بانکی
                      </span>
                    }
                  >
                    <Select
                      placeholder="انتخاب حساب بانکی"
                      style={{ fontFamily: 'CustomFont' }}
                      allowClear
                    >
                      {banks.map((bank) => (
                        <Option key={bank.id} value={bank.id}>
                          {bank.bank_name || 'صندوق نقدی'}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <Form.Item
                  name="description"
                  label={
                    <span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>
                  }
                >
                  <TextArea
                    rows={3}
                    placeholder="توضیحات اختیاری..."
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Form.Item>

                <Form.Item
                  name="bill"
                  label={
                    <span style={{ fontFamily: 'CustomFont' }}>
                      فاکتور / رسید
                    </span>
                  }
                >
                  <Upload
                    beforeUpload={() => false}
                    onChange={handleBillUpload}
                    maxCount={1}
                    accept="image/*,.pdf"
                    listType="picture"
                  >
                    <Button
                      icon={<UploadOutlined />}
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      انتخاب فایل
                    </Button>
                  </Upload>
                </Form.Item>

                <div className="flex justify-end gap-4 mt-6">
                  <Button
                    icon={<CloseOutlined />}
                    onClick={() => navigate('/finance/expenses')}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    انصراف
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={submitting}
                    className="bg-green-600 hover:bg-green-700 border-0"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    ذخیره
                  </Button>
                </div>
              </Form>
            </Card>
          </div>
        </Card>

        {/* Category Management Modal */}
        <Modal
          title={
            <span style={{ fontFamily: 'CustomFont' }}>
              مدیریت دسته‌بندی هزینه‌ها
            </span>
          }
          open={categoryModalVisible}
          onCancel={() => {
            setCategoryModalVisible(false);
            setEditingCategoryId(null);
            categoryForm.resetFields();
          }}
          footer={null}
          width={700}
          destroyOnClose
        >
          <Spin spinning={categoryLoading}>
            {/* فرم ایجاد / ویرایش */}
            <Card
              className="mb-4"
              size="small"
              title={
                <span style={{ fontFamily: 'CustomFont' }}>
                  {editingCategoryId
                    ? 'ویرایش دسته‌بندی'
                    : 'افزودن دسته‌بندی جدید'}
                </span>
              }
            >
              <Form
                form={categoryForm}
                layout="vertical"
                onFinish={handleCreateCategory}
                scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="category_name"
                    label={
                      <span style={{ fontFamily: 'CustomFont' }}>
                        نام دسته‌بندی
                      </span>
                    }
                    rules={[
                      {
                        required: true,
                        message: 'لطفاً نام دسته‌بندی را وارد کنید',
                      },
                    ]}
                  >
                    <Input
                      placeholder="نام دسته‌بندی"
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>

                  {!editingCategoryId && (
                    <Form.Item
                      name="roles"
                      label={
                        <span style={{ fontFamily: 'CustomFont' }}>
                          نقش‌ها
                        </span>
                      }
                    >
                      <Select
                        mode="multiple"
                        placeholder="انتخاب نقش‌ها"
                        style={{ fontFamily: 'CustomFont' }}
                        allowClear
                      >
                        {allRoles.map((role) => (
                          <Option key={role.id} value={role.id}>
                            {translateRoleName(role.display_name || role.name)}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={categorySubmitting}
                    icon={editingCategoryId ? <EditOutlined /> : <PlusOutlined />}
                    className="bg-green-600 hover:bg-green-700 border-0"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {editingCategoryId ? 'ویرایش' : 'افزودن'}
                  </Button>
                  {editingCategoryId && (
                    <Button
                      onClick={cancelEditCategory}
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      انصراف
                    </Button>
                  )}
                </div>
              </Form>
            </Card>

            {/* جدول دسته‌بندی‌ها */}
            <Table
              columns={categoryColumns}
              dataSource={allCategories}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Spin>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default AddExpense;
